/**
 * GuestFormDialog
 *
 * Unified create/edit dialog for guests.
 * - guest = undefined → create mode
 * - guest = Guest → edit mode
 *
 * Respects editPolicy from the backend:
 * - editPolicy='bl-read-only' → shows banner, disables fields
 * - editPolicy='native' or undefined → fields enabled
 */

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/core';
import { Alert, AlertDescription } from '@/theme/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '@/theme/components/ui/form';
import { Input } from '@/theme/components/ui/input';
import { notify } from '@/shared/services';
import { useUpdateGuest } from '../hooks/useGuests';
import { createGuestWithPerson } from '../services/guestService';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import type { Guest, GuestFormData } from '../types';
import type { Person, PersonFormData } from '@/entities/person';

interface GuestFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guest?: Guest;
  guestSourceType?: Guest['guestSourceType'];
  onSuccess?: (guest: Guest) => void;
}

const guestFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  documentId: z.string().optional().or(z.literal('')),
  dateOfBirth: z.string().optional().or(z.literal('')),
  nationality: z.string().optional().or(z.literal('')),
});

type GuestFormValues = z.infer<typeof guestFormSchema>;

function buildDefaults(guest?: Guest): GuestFormValues {
  if (!guest) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      documentId: '',
      dateOfBirth: '',
      nationality: '',
    };
  }
  return {
    firstName: guest.firstName,
    lastName: guest.lastName,
    email: guest.email,
    phone: guest.phone || '',
    documentId: guest.person?.documentNumber || '',
    dateOfBirth: guest.person?.birthDate || '',
    nationality: guest.nationality || '',
  };
}

export function GuestFormDialog({
  open,
  onOpenChange,
  guest,
  guestSourceType = 'booking',
  onSuccess,
}: GuestFormDialogProps) {
  const { t } = useTranslation();
  const { activeLocationId: locationId } = useActiveContext();
  const updateMutation = useUpdateGuest();

  const isEdit = !!guest;
  const isReadOnly = guest?.editPolicy === 'bl-read-only';

  const form = useForm<GuestFormValues>({
    resolver: zodResolver(guestFormSchema),
    defaultValues: buildDefaults(guest),
  });

  const { formState: { isSubmitting }, reset } = form;

  // Reset form when guest changes
  useEffect(() => {
    reset(buildDefaults(guest));
  }, [guest, reset]);

  const onSubmit = async (data: GuestFormValues) => {
    try {
      if (isEdit && guest) {
        // Edit existing guest. GuestFormData uses documentNumber/birthDate —
        // the zod form collects these under friendlier labels (documentId,
        // dateOfBirth) so we map them at the boundary, matching how the
        // create branch below does the same.
        const updated = await updateMutation.mutateAsync({
          guestId: guest.id,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            documentNumber: data.documentId || undefined,
            birthDate: data.dateOfBirth || undefined,
            nationality: data.nationality,
          },
          personId: guest.person?.id,
        });
        notify.success(t('guests.form.submitEdit', 'Guest updated successfully'));
        onOpenChange(false);
        onSuccess?.(updated);
      } else {
        // Create new guest
        if (!locationId) {
          notify.error(t('common.error', 'Error'), {
            description: t('guests.form.noLocationSelected', 'No location selected'),
          });
          return;
        }

        // Persona + ficha en UNA transacción (PENDINGS #71).
        //
        // Antes eran dos llamadas encadenadas: `createPerson` y después
        // `createGuestFromPerson`. Si la segunda fallaba —conflicto de rol, un
        // timeout— la persona ya estaba escrita, pero el `catch` de abajo
        // mostraba un error genérico y el recepcionista entendía,
        // razonablemente, que no se había creado nada. Volvía a pulsar "Crear"
        // y salía una SEGUNDA persona con el mismo nombre.
        const personData: PersonFormData = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          nationality: data.nationality || undefined,
          birthDate: data.dateOfBirth || undefined,
          documentNumber: data.documentId || undefined,
        };

        const newGuest = await createGuestWithPerson({
          person: personData,
          locationId,
          guestSourceType,
        });

        notify.success(t('guests.form.submitCreate', 'Guest created successfully'));
        onOpenChange(false);
        onSuccess?.(newGuest);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('common.unknownError', 'Unknown error');
      notify.error(t('common.error', 'Error'), {
        description: message,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-[20px] font-semibold tracking-[-0.01em]">
            {isEdit
              ? t('guests.form.editTitle', 'Edit guest')
              : t('guests.form.createTitle', 'New guest')}
          </DialogTitle>
          {isEdit && (
            <DialogDescription>
              {t('guests.form.editDescription', 'Update guest information')}
            </DialogDescription>
          )}
        </DialogHeader>

        {isReadOnly && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              {t('guests.form.blReadOnly', 'This guest is owned by Bookinglayer and cannot be edited.')}
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.firstNameLabel', 'First name')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.lastNameLabel', 'Last name')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.emailLabel', 'Email')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.phoneLabel', 'Phone (optional)')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="documentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.documentIdLabel', 'Document ID (optional)')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.dateOfBirthLabel', 'Date of birth (optional)')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nationality"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('guests.form.nationalityLabel', 'Nationality (optional)')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isReadOnly || isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || isReadOnly}>
                {isSubmitting
                  ? t('common.saving', 'Saving…')
                  : isEdit
                    ? t('guests.form.submitEdit', 'Save changes')
                    : t('guests.form.submitCreate', 'Create guest')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
